// Copyright ©️ 2026 eVolve MEP, LLC

import { type BenderProfile, type MachineProfile, useEvolvePaginatedGet } from '@evolvemep/api-ts';

import { SearchableSelect } from 'components/Mantine/TypeSafeSelect/PaginatedSelectors/SearchableSelect';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { BenderProfileOption } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/BenderProfileOption';
import {
  extractConduitSizes,
  extractConduitTypes,
} from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/helpers';
import { useBendCreatorFormContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorFormContext';
import { useBendCreatorValidationContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorValidationContext';

type Props = {
  getSpecificProfile: (machineProfile: MachineProfile) => Promise<BenderProfile>;
  resetSpecificProfile: () => void;
};

export const BendMachineProfileSelect = ({ getSpecificProfile, resetSpecificProfile }: Props) => {
  const fetchProfiles = useEvolvePaginatedGet('shop/fabrication/machineProfile');
  const { values, setFieldValue } = useBendCreatorFormContext();
  const { validateBend, resetLastValidation, wrappedGetInputProps } = useBendCreatorValidationContext();

  return (
    <SearchableSelect
      paginatedGet={fetchProfiles}
      idKey="machineProfileId"
      label="Bender Profile"
      getItemLabel={({ name }) => name}
      renderOption={BenderProfileOption}
      alwaysShowWhenLoading
      required
      placeholder="Select a bender"
      {...wrappedGetInputProps('benderProfile')}
      onChange={(v: MachineProfile | null) => {
        setFieldValue('benderProfile', v);
        if (isNil(v)) {
          resetLastValidation();
          return;
        }
        void getSpecificProfile(v)
          .then((settings) => {
            const conduitTypeIds = extractConduitTypes(settings);
            if (isNotNil(values.conduitTypeId) && conduitTypeIds.includes(values.conduitTypeId)) {
              const conduitSizeIds = extractConduitSizes(settings, values.conduitTypeId);
              if (isNotNil(values.conduitSizeId) && conduitSizeIds.includes(values.conduitSizeId)) {
                return;
              }
              setFieldValue('conduitSizeId', null);
              resetLastValidation();
              return;
            }
            setFieldValue('conduitTypeId', null);
            resetLastValidation();
          })
          .catch(() => {
            resetSpecificProfile();
          });
        void validateBend('benderProfile', v);
      }}
    />
  );
};
